// src/components/LayerPanel/MyLayersTab.jsx
//
// MY LAYERS scope — user uploads and drawn layers. Create + Import affordances
// sit at the top; the list of layers (own, then team-shared) sits below, with
// a per-tab search that filters by uploaded name.
//
// UserLayerRow and the catalog body are ported from the old LayerPanel
// largely verbatim — the additions are the per-tab search and the use of the
// shared layerBoundsFlat util instead of the panel-local bounds helper.

import { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Upload, Pencil, Eye, LocateFixed, Trash2, X, Search } from "lucide-react";

import { useUserLayersStore } from "../../stores/useUserLayersStore";
import { useLayerRegistryStore } from "../../stores/useLayerRegistryStore";
import { useMapActions } from "../../stores/useMapActions";
import { useAnnotationStore } from "../../stores/useAnnotationStore";
import { layerBoundsFlat, bboxesIntersect } from "../../utils/aoiBounds";
import { useOperatingAoiContext } from "../../stores/useOperatingAoiContext";
import { exportGeoJSON, exportKML, exportShapefile } from "../../utils/exportLayerUtils";
import LayerImportDropzone from "../LayerImportDropzone";
import ToggleSwitch from "./ToggleSwitch";

function cx(...c) { return c.filter(Boolean).join(" "); }

// Partition a list of user layers into "visible in current workbench"
// vs "hidden in another workbench" based on bbox intersection.
//   - No active workbench → everything visible, zero hidden.
//   - Layer has a usable bbox → intersection test decides visibility.
//   - Layer has NO usable bbox (no geometry client-side, malformed
//     coords) → ALWAYS visible. Don't hide layers we can't evaluate.
// Returns destructured names matching the "Own" list; the Shared list
// renames at the callsite via destructuring.
function partitionLayersByOperatingAoi(layers, opBbox) {
  if (!opBbox) return { visibleOwn: layers, hiddenOwnCount: 0 };
  const visible = [];
  let hidden = 0;
  for (const l of layers) {
    const b = layerBoundsFlat(l);
    if (!b || bboxesIntersect(b, opBbox)) {
      visible.push(l);
    } else {
      hidden += 1;
    }
  }
  return { visibleOwn: visible, hiddenOwnCount: hidden };
}

/* ─────────────────────────────────────────────
   MY LAYERS — COLOR SWATCH
   Same construction as FeatureColorSwatch: a colored box with a transparent
   <input type="color"> stretched over it, so the box IS the control and the
   browser supplies the picker. It shows the layer's effective color, so it
   doubles as the row's legend. setLayerColor repaints the map on every change
   and saves once the user settles.
───────────────────────────────────────────── */
function UserLayerColorSwatch({ color, name, onChange }) {
  return (
    <label
      className="relative cursor-pointer leading-none flex-shrink-0"
      title="Set color"
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <span
        style={{ background: color }}
        className="block w-2.5 h-2.5 rounded-sm border border-slate-600"
      />
      <input
        type="color"
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        aria-label={`Color for ${name}`}
      />
    </label>
  );
}

/* ─────────────────────────────────────────────
   MY LAYERS — LAYER ROW
───────────────────────────────────────────── */
function UserLayerRow({
  dl, isOwn, registryLayers, toggleLayer, zoomToBounds,
  shareLayer, unshareLayer, deleteLayer, renamingId, setRenamingId,
  renameVal, setRenameVal, renameLayer, onEditLayer, setLayerColor,
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [exporting, setExporting] = useState(null);
  const menuRef = useRef(null);

  const fillId = `user-layer-${dl.id}-fill`;
  const lineId = `user-layer-${dl.id}-line`;
  const on = !!registryLayers[fillId]?.visible;
  const shared = dl.visibility === "project";

  // Recolorable when it is your own imported layer AND it is drawn in one
  // color. A layer whose features carry their own `color` (a re-imported
  // annotation export) gets a per-feature expression here instead of a
  // string, and a layer-level color would visibly do nothing on it. Drawn
  // layers get their colors in the annotation panel.
  const layerFill = registryLayers[fillId]?._fillColor;
  const recolorable = isOwn && dl.layer_type === "imported" && typeof layerFill === "string";

  // Close the export menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    function onOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [menuOpen]);

  function handleToggle() {
    if (registryLayers[fillId]) toggleLayer(fillId);
    if (registryLayers[lineId]) toggleLayer(lineId);
  }

  function handleZoom() {
    try {
      const b = layerBoundsFlat(dl);
      if (!b) return;
      zoomToBounds(b);
    } catch (e) {
      console.warn("[UserLayerRow] zoom failed", e);
    }
  }

  function handleContextMenu(e) {
    e.preventDefault();
    e.stopPropagation();
    setMenuPos({ x: e.clientX, y: e.clientY });
    setMenuOpen(true);
  }

  async function handleExport(format) {
    setExporting(format);
    setMenuOpen(false);
    try {
      const geojson = dl.geojson;
      if (!geojson) throw new Error("No geometry data");
      if (format === "geojson") exportGeoJSON(geojson, dl.name);
      if (format === "kml") exportKML(geojson, dl.name);
      if (format === "shp") await exportShapefile(geojson, dl.name);
    } catch (err) {
      console.error("[export]", err);
    } finally {
      setExporting(null);
    }
  }

  const hasGeometry = (() => {
    try { return !!layerBoundsFlat(dl); } catch { return false; }
  })();

  // Context menu — portaled to body to escape overflow/transform ancestors.
  const contextMenu = menuOpen ? createPortal(
    <div
      ref={menuRef}
      style={{ position: "fixed", top: menuPos.y, left: menuPos.x, zIndex: 99999 }}
      className="min-w-[160px] bg-bg-softer border border-slate-700 rounded-md shadow-xl shadow-black/60 py-1 text-[11px]"
    >
      <div className="px-3 py-1.5 border-b border-slate-700/60 mb-1">
        <div className="text-[10px] font-semibold text-gray-300 truncate max-w-[140px]">{dl.name}</div>
        <div className="text-[9px] text-fg-secondary font-mono uppercase tracking-wide mt-0.5">Export layer</div>
      </div>
      {[
        { fmt: "geojson", ext: ".geojson", label: "GeoJSON" },
        { fmt: "kml", ext: ".kml", label: "KML" },
        { fmt: "shp", ext: ".zip", label: "Shapefile" },
      ].map(({ fmt, ext, label }) => (
        <button
          key={fmt}
          onClick={() => handleExport(fmt)}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-gray-300 hover:bg-slate-700 hover:text-white transition"
        >
          <span className="text-[9px] font-mono w-14 text-accent-action flex-shrink-0">{ext}</span>
          {label}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div
        className={cx(
          "group flex items-center gap-2 px-2 py-[7px] rounded border transition",
          on ? "bg-bg-softer border-slate-700" : "bg-bg border-slate-800 hover:border-slate-700"
        )}
        onContextMenu={handleContextMenu}
      >
        {on ? (
          <span className="w-1.5 h-1.5 rounded-full bg-accent-info flex-shrink-0" />
        ) : dl.layer_type === "imported" ? (
          <Upload size={11} className="text-fg-secondary flex-shrink-0" />
        ) : (
          <Pencil size={11} className="text-fg-secondary flex-shrink-0" />
        )}

        {recolorable && (
          <UserLayerColorSwatch
            color={layerFill}
            name={dl.name}
            onChange={(color) => setLayerColor(dl.id, color)}
          />
        )}

        {renamingId === dl.id ? (
          <div className="flex-1 flex items-center gap-1 min-w-0">
            <input
              autoFocus
              value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter") { await renameLayer(dl.id, renameVal); setRenamingId(null); }
                if (e.key === "Escape") setRenamingId(null);
              }}
              onBlur={() => setRenamingId(null)}
              className="flex-1 min-w-0 bg-bg-softer border border-slate-700 rounded px-1 py-px text-[11px] text-gray-200 focus:outline-none focus:border-accent-action/60"
            />
            <button
              onMouseDown={async (e) => {
                e.preventDefault();
                await renameLayer(dl.id, renameVal);
                setRenamingId(null);
              }}
              className="flex-shrink-0 p-0.5 rounded bg-accent-action/20 hover:bg-accent-action/30 text-accent-action border border-accent-action/30 transition"
            >
              <Eye size={10} />
            </button>
          </div>
        ) : (
          <button
            onClick={handleToggle}
            onDoubleClick={() => {
              if (!isOwn) return;
              setRenamingId(dl.id);
              setRenameVal(dl.name);
            }}
            title={isOwn ? "Double-click to rename · Right-click to export" : dl.name}
            className={cx(
              "flex-1 text-left text-[11px] truncate",
              on ? "text-gray-200" : "text-gray-400 hover:text-gray-200"
            )}
          >
            {dl.name}
          </button>
        )}

        {dl.source_format && (
          <span className="text-[8px] font-mono text-fg-secondary flex-shrink-0 uppercase">
            {dl.source_format}
          </span>
        )}

        {exporting && (
          <span className="text-[9px] font-mono text-fg-secondary flex-shrink-0 animate-pulse">
            {exporting}…
          </span>
        )}

        {hasGeometry && (
          <button
            onClick={handleZoom}
            title="Zoom to layer"
            className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-slate-700 text-fg-secondary hover:text-accent-action transition"
          >
            <LocateFixed size={11} />
          </button>
        )}

        {/* Reopen a drawn layer in the annotation panel.

            A button rather than a click on the row: every gesture the row has
            is already spoken for — click toggles visibility, double-click
            renames, right-click exports — and visibility is the row's main
            job, so editing does not get to take it.

            Drawn layers only, and only your own. An imported shapefile can
            carry thousands of features with arbitrary properties; loading one
            into the annotation session would be slow, and saving would
            rewrite its properties into the annotation shape, quietly damaging
            the source data. Import stays export/rename/delete. */}
        {isOwn && dl.layer_type === "drawn" && hasGeometry && (
          <button
            onClick={() => onEditLayer(dl)}
            title="Edit in annotation panel"
            className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-slate-700 text-fg-secondary hover:text-accent-action transition"
          >
            <Pencil size={11} />
          </button>
        )}

        {isOwn && (
          <button
            onClick={() => (shared ? unshareLayer(dl.id) : shareLayer(dl.id))}
            title={shared ? "Shared — click to make private" : "Private — click to share"}
            className={cx(
              "flex-shrink-0 flex items-center gap-1 text-[8px] font-mono px-1.5 py-px rounded transition",
              shared
                ? "bg-accent-action/15 text-accent-action border border-accent-action/30 hover:bg-accent-action/25"
                : "bg-slate-800 text-fg-secondary border border-slate-700 hover:text-gray-400"
            )}
          >
            {shared ? "Shared" : "Private"}
          </button>
        )}

        {!isOwn && (
          <span className="text-[8px] font-mono text-fg-secondary flex-shrink-0 px-1">shared</span>
        )}

        <button onClick={handleToggle} className="flex-shrink-0">
          <ToggleSwitch on={on} />
        </button>

        {isOwn && !confirmDelete && (
          <button
            onClick={() => setConfirmDelete(true)}
            className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-slate-700 text-fg-secondary hover:text-red-400 transition"
          >
            <Trash2 size={10} />
          </button>
        )}
        {isOwn && confirmDelete && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              onClick={() => { deleteLayer(dl.id); setConfirmDelete(false); }}
              className="px-1.5 py-px rounded bg-red-500/20 border border-red-500/40 text-[9px] text-red-400 hover:bg-red-500/30 transition font-mono"
            >
              del
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="p-0.5 rounded hover:bg-slate-700 text-fg-secondary hover:text-gray-300 transition"
            >
              <X size={10} />
            </button>
          </div>
        )}
      </div>

      {contextMenu}
    </>
  );
}

/* ─────────────────────────────────────────────
   MY LAYERS TAB
───────────────────────────────────────────── */
export default function MyLayersTab() {
  const userLayers = useUserLayersStore((s) => s.layers);
  const sharedLayers = useUserLayersStore((s) => s.shared);
  const loading = useUserLayersStore((s) => s.loading);
  const shareLayer = useUserLayersStore((s) => s.shareLayer);
  const unshareLayer = useUserLayersStore((s) => s.unshareLayer);
  const deleteLayer = useUserLayersStore((s) => s.deleteLayer);
  const renameLayer = useUserLayersStore((s) => s.renameLayer);
  const setLayerColor = useUserLayersStore((s) => s.setLayerColor);

  const registryLayers = useLayerRegistryStore((s) => s.layers);
  const toggleLayer = useLayerRegistryStore((s) => s.toggleLayer);
  const zoomToBounds = useMapActions((s) => s.zoomToBounds);

  const openAnnotationPanel = useAnnotationStore((s) => s.openPanel);
  const openAnnotationPanelForLayer = useAnnotationStore((s) => s.openPanelForLayer);
  const closeAnnotationPanel = useAnnotationStore((s) => s.closePanel);
  // The panel's own X closes it without coming through this component, so its
  // open state is the store's, not a local mode flag. Mirroring it locally is
  // what let the two drift: Create could only ever open, never close.
  const annotationOpen = useAnnotationStore((s) => s.open);

  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [mode, setMode] = useState(null); // null | "import" — create lives in the store
  const [search, setSearch] = useState("");

  function toggleMode(next) {
    if (next === "create") {
      // Toggle the panel itself. Reading annotationOpen rather than a local
      // flag means the button still works after the panel's X was used.
      if (annotationOpen) closeAnnotationPanel();
      else { setMode(null); openAnnotationPanel(); }
      return;
    }
    setMode((prev) => (prev === next ? null : next));
  }

  const othersShared = useMemo(
    () => (sharedLayers ?? []).filter((l) => !l.is_owner && l.visibility === "project"),
    [sharedLayers]
  );

  // Per-tab search: filter both lists by uploaded name.
  const q = search.trim().toLowerCase();
  const filteredOwn = useMemo(
    () => (q ? userLayers.filter((l) => l.name?.toLowerCase().includes(q)) : userLayers),
    [userLayers, q]
  );
  const filteredShared = useMemo(
    () => (q ? othersShared.filter((l) => l.name?.toLowerCase().includes(q)) : othersShared),
    [othersShared, q]
  );

  // Aggressive operating-AOI filter — when a workbench is active, only
  // layers whose bbox intersects the workbench bbox are shown. Layers
  // with no computable bbox (no geometry client-side, malformed
  // coordinates) are treated as ALWAYS VISIBLE — better to show a
  // layer we can't evaluate than to hide it incorrectly.
  const activeOperatingAoiId = useOperatingAoiContext((s) => s.activeOperatingAoiId);
  const operatingAois = useOperatingAoiContext((s) => s.operatingAois);
  const activeOperatingAoiBbox = activeOperatingAoiId
    ? operatingAois.find((a) => a.id === activeOperatingAoiId)?.bounds ?? null
    : null;

  const { visibleOwn, hiddenOwnCount } = useMemo(
    () => partitionLayersByOperatingAoi(filteredOwn, activeOperatingAoiBbox),
    [filteredOwn, activeOperatingAoiBbox]
  );
  const { visibleShared, hiddenSharedCount } = useMemo(() => {
    const r = partitionLayersByOperatingAoi(filteredShared, activeOperatingAoiBbox);
    return { visibleShared: r.visibleOwn, hiddenSharedCount: r.hiddenOwnCount };
  }, [filteredShared, activeOperatingAoiBbox]);

  const hiddenTotal = hiddenOwnCount + hiddenSharedCount;

  const rowProps = {
    registryLayers, toggleLayer, zoomToBounds, shareLayer, unshareLayer,
    deleteLayer, renamingId, setRenamingId, renameVal, setRenameVal, renameLayer, setLayerColor,
    // Reopening a saved layer replaces whatever is in the panel, so it also
    // leaves the import/create mode the tab may be sitting in — otherwise the
    // dropzone stays open behind an editor that has nothing to do with it.
    onEditLayer: (dl) => { setMode(null); openAnnotationPanelForLayer(dl); },
  };

  if (loading) {
    return (
      <div className="px-3 py-3 text-[10px] text-fg-secondary animate-pulse">
        Loading your layers…
      </div>
    );
  }

  return (
    <div className="px-2 py-2 space-y-1">
      {/* Create / Import */}
      <div className="flex gap-1.5 mb-1">
        <button
          onClick={() => toggleMode("create")}
          className={cx(
            "flex-1 flex items-center justify-center gap-1.5 px-2 py-[6px] rounded-md border text-[10px] transition",
            annotationOpen
              ? "bg-accent-action/15 border-accent-action/50 text-accent-action"
              : "border-slate-700 text-fg-secondary hover:text-gray-300 hover:border-slate-500"
          )}
        >
          <Pencil size={11} />
          Create
        </button>
        <button
          onClick={() => toggleMode("import")}
          className={cx(
            "flex-1 flex items-center justify-center gap-1.5 px-2 py-[6px] rounded-md border text-[10px] transition",
            mode === "import"
              ? "bg-accent-action/15 border-accent-action/50 text-accent-action"
              : "border-slate-700 text-fg-secondary hover:text-gray-300 hover:border-slate-500"
          )}
        >
          <Upload size={11} />
          Import
        </button>
      </div>

      {mode === "import" && <LayerImportDropzone onDone={() => setMode(null)} />}

      {/* Per-tab search */}
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-secondary pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search my layers…"
          aria-label="Search my layers"
          className={cx(
            "w-full pl-6 pr-6 py-1.5 rounded-md border bg-bg text-[11px] text-gray-200",
            "placeholder-gray-600 focus:outline-none transition",
            search
              ? "border-accent-info/60 focus:border-accent-info"
              : "border-slate-700 focus:border-accent-info/60"
          )}
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-secondary hover:text-gray-200"
          >
            <X size={11} />
          </button>
        )}
      </div>

      {/* Empty state — no mode active and nothing uploaded yet. */}
      {!mode && !annotationOpen && userLayers.length === 0 && othersShared.length === 0 && (
        <div className="text-[10px] text-fg-secondary px-1 pb-1">
          Draw or import layers to add them here. Toggle to share with your
          project team.
        </div>
      )}

      {/* No-results state — there are layers, but none match the search. */}
      {q && filteredOwn.length === 0 && filteredShared.length === 0 &&
        (userLayers.length > 0 || othersShared.length > 0) && (
          <div className="text-[10px] text-fg-secondary px-1 pb-1">
            No layers match your search.
          </div>
        )}

      {/* Empty-after-workbench-filter state — there are layers, none match
          the current operating AOI. The "N in other workbenches" footer
          below makes the filtering effect explicit. */}
      {!q && activeOperatingAoiBbox
        && visibleOwn.length === 0 && visibleShared.length === 0
        && (filteredOwn.length > 0 || filteredShared.length > 0) && (
          <div className="text-[10px] text-fg-secondary px-1 pb-1">
            No My Layers in this operating AOI.
          </div>
        )}

      {visibleOwn.map((dl) => (
        <UserLayerRow key={dl.id} dl={dl} isOwn {...rowProps} />
      ))}

      {visibleShared.length > 0 && (
        <>
          <div className="text-[9px] font-mono uppercase tracking-[0.12em] text-fg-secondary px-1 pt-2 pb-1">
            Shared by team
          </div>
          {visibleShared.map((dl) => (
            <UserLayerRow key={dl.id} dl={dl} isOwn={false} {...rowProps} />
          ))}
        </>
      )}

      {/* Filter footer — informational only, no interaction. Renders when
          a workbench is active and at least one layer is hidden by its
          bbox not intersecting the workbench bbox. */}
      {hiddenTotal > 0 && (
        <div className="px-1 pt-2 text-[10px] text-fg-secondary italic">
          {hiddenTotal} layer{hiddenTotal === 1 ? "" : "s"} in other workbenches.
        </div>
      )}
    </div>
  );
}
